(() => {
  const FINGERPRINT_VERSION = 'v1';
  const GIFT_MESSAGE_PROPERTY = 'Gift Message';
  const GIFT_MESSAGE_ENABLED_PROPERTY = '_Gift Message Enabled';
  const GIFT_PROPERTIES = new Set([GIFT_MESSAGE_PROPERTY, GIFT_MESSAGE_ENABLED_PROPERTY]);
  const STORAGE_PREFIX = 'luminary:gift-message';
  const STORAGE_STATE_KEY = `${STORAGE_PREFIX}:state`;
  const CHECKOUT_ERROR = 'We could not save your Gift Message. Please try again.';

  class GiftMessageCart {
    constructor(root) {
      this.root = root;
      this.form = document.getElementById('cart');
      this.isReconciling = false;
      this.refreshQueued = false;
      this.serverCleanupPending = false;
      this.serverCleanupInFlight = false;
      this.serverCartReady = false;
      this.serverCartToken = '';
      this.activeServerFingerprints = new Set();
      this.root.addEventListener('change', this.onChange.bind(this));
      this.root.addEventListener('input', this.onInput.bind(this));
      document.addEventListener('submit', this.onSubmit.bind(this), true);
      document.addEventListener('click', this.onDocumentClick.bind(this), true);
      this.observeRenders();
      this.queueServerCleanup();
    }

    observeRenders() {
      const roots = [this.root, document.querySelector('cart-drawer-items')]
        .filter((root, index, allRoots) => root && allRoots.indexOf(root) === index);

      this.renderObservers = roots.map((root) => {
        const observer = new MutationObserver(() => this.queueHydrate());
        observer.observe(root, { childList: true, subtree: true });
        return observer;
      });
    }

    queueHydrate() {
      if (this.refreshQueued) return;
      this.refreshQueued = true;
      queueMicrotask(() => {
        this.refreshQueued = false;
        this.queueServerCleanup();
      });
    }

    onDocumentClick(event) {
      const removeButton = event.target.closest('cart-remove-button');
      if (!removeButton) return;

      const line = removeButton.dataset.index;
      const lineElement = removeButton.closest('[data-gift-message-cart-item]')
        || document.getElementById(`CartItem-${line}`)
        || document.getElementById(`CartDrawer-Item-${line}`);
      const element = lineElement && lineElement.matches('[data-gift-message-cart-item]')
        ? lineElement
        : lineElement?.querySelector('[data-gift-message-cart-item]');
      if (!element) return;

      const context = this.getItemContext(element);
      this.waitForRemovedFingerprint(context.fingerprint);
    }

    getItems() {
      return Array.from(this.root.querySelectorAll('[data-gift-message-cart-item]'));
    }

    getItemContext(element) {
      const properties = this.parseProperties(element.dataset.properties);
      const fingerprint = this.createFingerprint({
        productId: element.dataset.productId,
        variantId: element.dataset.variantId,
        sellingPlanId: element.dataset.sellingPlanId,
        properties
      });

      return { fingerprint, properties };
    }

    getStorageState() {
      try {
        const state = JSON.parse(window.localStorage.getItem(STORAGE_STATE_KEY) || 'null');
        return state && typeof state.cartToken === 'string' && typeof state.empty === 'boolean' ? state : null;
      } catch (_) {
        return null;
      }
    }

    setStorageState(cartToken, empty) {
      try {
        window.localStorage.setItem(STORAGE_STATE_KEY, JSON.stringify({ cartToken, empty }));
      } catch (_) {
        // A storage restriction must not affect the existing cart flow.
      }
    }

    parseProperties(serializedProperties) {
      try {
        const properties = JSON.parse(serializedProperties || '{}');
        return properties && typeof properties === 'object' ? properties : {};
      } catch (_) {
        return {};
      }
    }

    createFingerprint({ productId, variantId, sellingPlanId, properties }) {
      const nonGiftProperties = Object.entries(properties || {})
        .filter(([key]) => !GIFT_PROPERTIES.has(key))
        .map(([key, value]) => [String(key), String(value)])
        .sort(([leftKey, leftValue], [rightKey, rightValue]) => leftKey.localeCompare(rightKey) || leftValue.localeCompare(rightValue));

      return JSON.stringify([
        FINGERPRINT_VERSION,
        String(productId || ''),
        String(variantId || ''),
        String(sellingPlanId || ''),
        nonGiftProperties
      ]);
    }

    getCartItemFingerprint(item) {
      return this.createFingerprint({
        productId: item.product_id,
        variantId: item.variant_id,
        sellingPlanId: item.selling_plan_allocation && item.selling_plan_allocation.selling_plan
          ? item.selling_plan_allocation.selling_plan.id
          : item.selling_plan,
        properties: item.properties
      });
    }

    storageKey(cartToken, fingerprint) {
      return `${STORAGE_PREFIX}:${cartToken}:${encodeURIComponent(fingerprint)}`;
    }

    clearCartDrafts(cartToken) {
      if (!cartToken) return;

      const prefix = `${STORAGE_PREFIX}:${cartToken}:`;
      try {
        const keysToRemove = [];
        for (let index = 0; index < window.localStorage.length; index += 1) {
          const key = window.localStorage.key(index);
          if (key && key.startsWith(prefix)) keysToRemove.push(key);
        }
        keysToRemove.forEach((key) => window.localStorage.removeItem(key));
      } catch (_) {
        // A storage restriction must not affect the existing cart flow.
      }
    }

    cleanupStaleDrafts(cartToken, activeFingerprints) {
      if (!cartToken) return;

      const prefix = `${STORAGE_PREFIX}:${cartToken}:`;
      try {
        const keysToRemove = [];
        for (let index = 0; index < window.localStorage.length; index += 1) {
          const key = window.localStorage.key(index);
          if (!key || !key.startsWith(prefix)) continue;

          let fingerprint;
          try {
            fingerprint = decodeURIComponent(key.slice(prefix.length));
          } catch (_) {
            continue;
          }

          if (!activeFingerprints.has(fingerprint)) keysToRemove.push(key);
        }
        keysToRemove.forEach((key) => window.localStorage.removeItem(key));
      } catch (_) {
        // A storage restriction must not affect the existing cart flow.
      }
    }

    queueServerCleanup() {
      this.serverCleanupPending = true;
      if (this.serverCleanupInFlight) return;

      this.serverCleanupInFlight = true;
      queueMicrotask(async () => {
        try {
          while (this.serverCleanupPending) {
            this.serverCleanupPending = false;
            try {
              await this.reconcileStorageWithServerCart();
            } catch (_) {
              // Never remove or invalidate drafts when /cart.js is unavailable.
            }
          }
        } finally {
          this.serverCleanupInFlight = false;
        }
      });
    }

    async reconcileStorageWithServerCart() {
      const cart = await this.fetchCart();
      const cartToken = String(cart.token || '');
      if (!cartToken) return;

      const state = this.getStorageState();
      if (state && state.cartToken !== cartToken) {
        this.clearCartDrafts(state.cartToken);
      }

      const activeFingerprints = this.getServerFingerprints(cart);
      this.serverCartReady = true;
      this.serverCartToken = cartToken;
      this.activeServerFingerprints = activeFingerprints;

      if (!activeFingerprints.size) {
        this.clearCartDrafts(cartToken);
        this.setStorageState(cartToken, true);
        return;
      }

      this.cleanupStaleDrafts(cartToken, activeFingerprints);
      this.setStorageState(cartToken, false);
      this.hydrate();
    }

    waitForRemovedFingerprint(fingerprint, attempt = 0) {
      const maxAttempts = 12;
      const delay = attempt === 0 ? 250 : 500;

      window.setTimeout(async () => {
        try {
          const cart = await this.fetchCart();
          const activeFingerprints = this.getServerFingerprints(cart);
          if (!activeFingerprints.has(fingerprint)) {
            this.applyServerCartCleanup(cart, activeFingerprints);
            return;
          }
        } catch (_) {
          // Retry while the cart update is still in flight.
        }

        if (attempt < maxAttempts) this.waitForRemovedFingerprint(fingerprint, attempt + 1);
      }, delay);
    }

    getServerFingerprints(cart) {
      return new Set((cart.items || []).map((item) => this.getCartItemFingerprint(item)));
    }

    applyServerCartCleanup(cart, activeFingerprints = this.getServerFingerprints(cart)) {
      const state = this.getStorageState();
      const cartToken = String(cart.token || state?.cartToken || '');
      if (!cartToken) return;

      this.serverCartReady = true;
      this.serverCartToken = cartToken;
      this.activeServerFingerprints = activeFingerprints;
      if (!activeFingerprints.size) {
        this.clearCartDrafts(cartToken);
        this.setStorageState(cartToken, true);
        return;
      }

      this.cleanupStaleDrafts(cartToken, activeFingerprints);
      this.setStorageState(cartToken, false);
      this.hydrate();
    }

    getDraft(context) {
      if (!this.serverCartReady || !this.activeServerFingerprints.has(context.fingerprint)) return null;

      try {
        const storedDraft = window.localStorage.getItem(this.storageKey(this.serverCartToken, context.fingerprint));
        if (!storedDraft) return null;
        const draft = JSON.parse(storedDraft);
        if (typeof draft.enabled !== 'boolean' || typeof draft.message !== 'string') return null;
        return draft;
      } catch (_) {
        return null;
      }
    }

    saveDraft(context, draft) {
      const cartToken = this.serverCartToken || this.getStorageState()?.cartToken;
      if (!cartToken) return;

      try {
        window.localStorage.setItem(this.storageKey(cartToken, context.fingerprint), JSON.stringify(draft));
      } catch (_) {
        // A storage restriction must not affect the existing cart flow.
      }
    }

    hydrate() {
      const elements = this.getItems();

      elements.forEach((element) => {
        const context = this.getItemContext(element);
        if (!this.activeServerFingerprints.has(context.fingerprint)) {
          this.setControls(element, { enabled: false, message: '' });
          return;
        }

        const draft = this.getDraft(context);
        const enabled = draft
          ? draft.enabled
          : String(context.properties[GIFT_MESSAGE_PROPERTY] || '').length > 0;
        const message = draft ? draft.message : String(context.properties[GIFT_MESSAGE_PROPERTY] || '');
        this.setControls(element, { enabled, message });
      });
    }

    setControls(element, { enabled, message }) {
      const checkbox = element.querySelector('[data-gift-message-enabled]');
      const field = element.querySelector('[data-gift-message-field]');
      const textarea = element.querySelector('[data-gift-message-text]');
      if (!checkbox || !field || !textarea) return;

      checkbox.checked = enabled;
      textarea.value = message;
      textarea.disabled = !enabled;
      field.hidden = !enabled;
      field.classList.toggle('hidden', !enabled);
    }

    onChange(event) {
      const checkbox = event.target.closest('[data-gift-message-enabled]');
      if (!checkbox || !this.root.contains(checkbox)) return;
      const element = checkbox.closest('[data-gift-message-cart-item]');
      const textarea = element.querySelector('[data-gift-message-text]');
      const draft = { enabled: checkbox.checked, message: textarea.value };

      this.saveDraftAndSync(element, draft);
    }

    onInput(event) {
      const textarea = event.target.closest('[data-gift-message-text]');
      if (!textarea || !this.root.contains(textarea)) return;
      const element = textarea.closest('[data-gift-message-cart-item]');
      const checkbox = element.querySelector('[data-gift-message-enabled]');
      this.saveDraftAndSync(element, { enabled: checkbox.checked, message: textarea.value });
    }

    saveDraftAndSync(element, draft) {
      const context = this.getItemContext(element);
      this.saveDraft(context, draft);
      this.getItems().forEach((candidate) => {
        if (this.getItemContext(candidate).fingerprint === context.fingerprint) {
          this.setControls(candidate, draft);
        }
      });
    }

    onSubmit(event) {
      if (event.target !== this.form || this.isReconciling) return;
      if (!event.submitter || event.submitter.name !== 'checkout') return;

      const drafts = this.getCurrentDrafts();
      if (!drafts.size) return;

      event.preventDefault();
      this.reconcileThenCheckout(event.submitter, drafts);
    }

    getCurrentDrafts() {
      const drafts = new Map();
      this.getItems().forEach((element) => {
        const context = this.getItemContext(element);
        const draft = this.getDraft(context);
        if (draft) drafts.set(context.fingerprint, draft);
      });
      return drafts;
    }

    async reconcileThenCheckout(submitter, drafts) {
      this.isReconciling = true;
      submitter.disabled = true;
      this.clearError();

      try {
        const cart = await this.reconcile(drafts);
        const cartToken = String(cart.token || this.serverCartToken || '');
        this.clearCartDrafts(cartToken);
        if (cartToken) this.setStorageState(cartToken, true);
        const checkoutIntent = document.createElement('input');
        checkoutIntent.type = 'hidden';
        checkoutIntent.name = 'checkout';
        checkoutIntent.value = '';
        this.form.appendChild(checkoutIntent);
        HTMLFormElement.prototype.submit.call(this.form);
      } catch (_) {
        submitter.disabled = false;
        this.showError();
        this.isReconciling = false;
      }
    }

    async reconcile(drafts) {
      let cart = await this.fetchCart();
      let safetyLimit = cart.items.length + drafts.size + 1;

      while (safetyLimit > 0) {
        const line = cart.items.find((item) => {
          const fingerprint = this.getCartItemFingerprint(item);
          const draft = drafts.get(fingerprint);
          return draft && !this.propertiesMatch(item.properties, this.reconciledProperties(item.properties, draft));
        });

        if (!line) return cart;

        const fingerprint = this.getCartItemFingerprint(line);
        await this.changeLine(line, this.reconciledProperties(line.properties, drafts.get(fingerprint)));
        cart = await this.fetchCart();
        safetyLimit -= 1;
      }

      throw new Error('Gift Message reconciliation did not complete.');
    }

    reconciledProperties(serverProperties, draft) {
      const properties = {};
      Object.entries(serverProperties || {}).forEach(([key, value]) => {
        if (!GIFT_PROPERTIES.has(key)) properties[key] = value;
      });

      if (draft.enabled && String(draft.message || '').trim()) {
        properties[GIFT_MESSAGE_PROPERTY] = draft.message;
      }

      return properties;
    }

    propertiesMatch(left, right) {
      const normalize = (properties) => Object.entries(properties || {})
        .map(([key, value]) => [String(key), String(value)])
        .sort(([leftKey, leftValue], [rightKey, rightValue]) => leftKey.localeCompare(rightKey) || leftValue.localeCompare(rightValue));
      return JSON.stringify(normalize(left)) === JSON.stringify(normalize(right));
    }

    async fetchCart() {
      const response = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error('Unable to load cart.');
      return response.json();
    }

    async changeLine(line, properties) {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: line.key, quantity: line.quantity, properties })
      });
      if (!response.ok) throw new Error('Unable to update cart line.');
      return response.json();
    }

    clearError() {
      const error = document.getElementById('cart-errors');
      if (error) error.textContent = '';
    }

    showError() {
      const error = document.getElementById('cart-errors');
      if (error) error.textContent = CHECKOUT_ERROR;
    }
  }

  const initialise = () => {
    const root = document.querySelector('cart-items');
    if (root && !root.giftMessageCart) root.giftMessageCart = new GiftMessageCart(root);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialise, { once: true });
  } else {
    initialise();
  }
})();
